import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getAddressOfOrder, getTicketTypeById, getEventInfosByEventId, getCountries, getTicketTypeInfosByTicketTypeId } from "ticketino-api-client";
import { motion } from "framer-motion";

import imgIconInfo from "../images/icons/info.svg";

import Header from "./components/Header";
import Loading from "./components/Loading";

const Shipping = () => {
    const [order, setOrder] = useState({});
    const [orderId] = useState(localStorage.getItem("orderId"));
    const [tickets, setTickets] = useState([]);
    const [organizerId, setOrganizerId] = useState(0);
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [buyerInfo, setBuyerInfo] = useState(() => {
        // Try to load from localStorage on first render
        const orderId = localStorage.getItem("orderId");
        const sessionBuyerInfo = orderId && localStorage.getItem(`buyerInfo_${orderId}`);
        if (sessionBuyerInfo) {
            return JSON.parse(sessionBuyerInfo);
        }
        return {
            salutation: "",
            firstName: "",
            name: "",
            street: "",
            city: "",
            postCode: "",
            countryId: 176,
            email: "",
            confirmemail: ""
            /*newsletter: false*/
        };
    });

    const eventId = localStorage.getItem("EventId");
    const eventName = localStorage.getItem("EventName");
    const eventDate = localStorage.getItem("EventDate");
    const eventStartTime = localStorage.getItem("EventStartTime");
    const eventCity = localStorage.getItem("EventCity");
    const eventLocation = localStorage.getItem("EventLocation");

    // react hook for navigation
    let navigate = useNavigate();

    // base url
    let baseUrl = process.env.REACT_APP_BASEURL_API;

    // setting the base url of the npm package api calls
    api.defaults.baseURL = baseUrl;

    // fetching resources
    const [resources, setResources] = useState({});
    const [countries, setCountries] = useState([]);

    // fetching params
    let { language } = useParams();

    let languageId = 0;

    // changing languageId according to the url
    switch (language) {
        case ("de" || "DE"):
            languageId = 1;
            break;
        case ("en" || "EN"):
            languageId = 3;
            break;
        default:
            languageId = 0;
            break;
    }

    const [tokenReady, setTokenReady] = useState(false);

    useEffect(() => {
        const loadToken = async () => {
            try {
                const res = await axios.get("form/token");
                axios.defaults.headers.common["Authorization"] = "Bearer " + res.data;
                api.defaults.headers.common['Authorization'] = "Bearer " + res.data;
                await localStorage.setItem("token", res.data);
                setTokenReady(true); // Set tokenReady after token is set

                requestFormSettings();
            } catch (error) {
                console.error(error);
            }
        };

        const requestFormSettings = async () => {
            try {
                const res = await axios.get(`form/formsettings`);

                if (orderId > 0) {
                    loadOrder(orderId)
                } else {
                    localStorage.removeItem("addedPromotion"); // clear promotion code
                    navigate(`/${language}/home/${eventId}`);
                }

                setOrganizerId(res.data.organizerId)
            } catch (error) {
                console.error(error);
            }
        };

        loadToken();
    }, []);

    // Only call requestResources and loadCountries when tokenReady is true and language changes
    useEffect(() => {
        if (tokenReady) {
            requestResources();
            loadCountries();
        }
    }, [language, tokenReady]);

    //redirect to home when there are no tickets in the order
    useEffect(() => {
        if (order.tickets && order.tickets.length === 0) {
            navigate(`/${language}/home/${eventId}`);
        }
    }, [tickets]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const removePromotionCode = async (promotion) => {
        try {
            const response = axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/PromotionCode/${promotion}`);
            return response.data;
        } catch (error) {
            console.error(error);
        }
    }

    const loadCountries = async () => {
        try {
            let countries = await getCountries(language);

            const indexCH = countries.findIndex(c => c.id === 176);
            const ch = countries[indexCH];
            countries.splice(indexCH, 1);

            var indexDE = countries.findIndex(c => c.id === 229);
            const de = countries[indexDE];
            countries.splice(indexDE, 1);

            var indexAU = countries.findIndex(c => c.id === 228);
            const au = countries[indexAU];
            countries.splice(indexAU, 1);

            var indexFR = countries.findIndex(c => c.id === 59);
            const fr = countries[indexFR];
            countries.splice(indexFR, 1);

            var indexIT = countries.findIndex(c => c.id === 87);
            const it = countries[indexIT];
            countries.splice(indexIT, 1);

            countries.unshift({ id: 0, name: "---------" });
            countries.unshift(it);
            countries.unshift(au);
            countries.unshift(fr);
            countries.unshift(de);
            countries.unshift(ch);

            setCountries(countries);
        }
        catch (error) {
            console.error(error);
        }
    }

    const getOrderByOrderId = async (orderId) => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            return res.data;
        }
        catch (error) {
            console.error(error);
            if (error.response.status === 404 || error.response.status === 403) {
                navigate(`/${language}/home/${eventId}`);
            }
        }
    }

    const loadOrder = async (orderId) => {
        try {
            // Order
            const updatedOrder = await getOrderByOrderId(orderId);
            const bookedTickets = updatedOrder.tickets;
            // Booked Tickets
            const updatedTickets = await Promise.all(
                bookedTickets.map(async (ticket) => {
                    const ticketType = await getTicketTypeById(ticket.ticketTypeId);
                    const ticketTypeInfoResp = await getTicketTypeInfosByTicketTypeId(ticket.ticketTypeId);
                    const ticketTypeInfo = ticketTypeInfoResp.find(info => info.languageId == languageId) ?? ticketTypeInfoResp[0];

                    const eventInfoResp = await getEventInfosByEventId(ticketType.eventId);
                    const eventInfo = eventInfoResp.eventInfos.find(info => info.languageId == languageId) ?? eventInfoResp.eventInfos[0];

                    return { ...ticket, ticketType: ticketType, ticketTypeInfo, eventInfo };
                })
            )
            setTickets(updatedTickets);
            setOrder(updatedOrder);

            // Prefill buyer info if address exists
            const address = await getAddressOfOrder(orderId);
            // Check for localStorage first
            const sessionBuyerInfo = localStorage.getItem(`buyerInfo_${orderId}`);
            if (sessionBuyerInfo) {
                setBuyerInfo(JSON.parse(sessionBuyerInfo));
            } else {
                setBuyerInfo({
                    ...buyerInfo,
                    ...address,
                    confirmemail: address.email
                    /*newsletter: address.code1 === "true" //convert to bool*/
                });
            }
        }
        catch (error) {
            console.error(error);
        }
    };

    const onInputChange = (e) => {
        setBuyerInfo({ ...buyerInfo, [e.target.name]: e.target.value });
    };

    // Refactored addAddressToBasket to accept a callback for navigation
    const addAddressToBasket = async (onSuccess) => {

        let addressBody = {
            salutation: buyerInfo.salutation,
            firstName: buyerInfo.firstName,
            name: buyerInfo.name,
            street: buyerInfo.street,
            city: buyerInfo.city,
            postCode: buyerInfo.postCode,
            countryId: buyerInfo.countryId,
            email: buyerInfo.email,
            /*code1: buyerInfo.newsletter,*/
        };

        try {
            await axios.put(`${baseUrl}/Order/${orderId}/Address`, addressBody);

            // Store the latest buyerInfo in localStorage.
            // This ensures that if the user returns to this page, the displayed data is up-to-date,
            // even if the API response is delayed and may not reflect the most recent changes yet.
            localStorage.setItem(`buyerInfo_${orderId}`,
                JSON.stringify({ ...buyerInfo })
            );

            //// gtm - addAddress
            //window.dataLayer = window.dataLayer || [];
            //window.dataLayer.push({
            //    'event': 'addAddress'
            //});

            if (onSuccess) onSuccess();
        } catch (error) {
            console.error(error.response?.data || error);
            navigate(`/${language}/home/${eventId}`);
        }
    };

    const onSubmit = async () => {
        const isValid = validateInputFields();

        if (!isValid) return;

        setLoading(true);

        await addAddressToBasket(() => {
            navigate(`/${language}/checkout`);
        });
    }

    //// Handler for BuyerBookMoreTickets button
    //const onBookMoreTickets = async () => {
    //    setLoading(true);
    //    await addAddressToBasket(() => {
    //        window.location.href = resources?.BuyerBookMoreTicketsUrl;
    //    });
    //}

    const validateInputFields = () => {
        const countryId = parseInt(buyerInfo.countryId, 10);

        let validation =
            countryId && countryId !== 0 && countryId !== 999 &&
            buyerInfo.salutation && /^.{1,70}$/.test(buyerInfo.salutation) &&
            buyerInfo.firstName && /^.{1,70}$/.test(buyerInfo.firstName) &&
            buyerInfo.name && /^.{1,70}$/.test(buyerInfo.name) &&
            buyerInfo.street && /^.{1,70}$/.test(buyerInfo.street) &&
            buyerInfo.postCode && /^.{1,70}$/.test(buyerInfo.postCode) &&
            buyerInfo.city && /^.{1,70}$/.test(buyerInfo.city) &&
            buyerInfo.email && /^\S+@\S+\.\S+$/.test(buyerInfo.email) &&
            buyerInfo.confirmemail && /^\S+@\S+\.\S+$/.test(buyerInfo.confirmemail) &&
            buyerInfo.email === buyerInfo.confirmemail;

        setIsError(!validation);
        return validation;

    }

    const removeTicketFromBasket = (id) => {
        let deleteBody = {
            ticketsToRemove: [id],
        };

        axios
            .delete(`${baseUrl}/ShopBasket/Order/${orderId}/Tickets`, {
                headers: {
                    Accept: "application/json",
                },
                data: deleteBody,
            })
            .then(() => loadOrder(orderId));
    }

    const onBackClick = async () => {
        navigate(-1)
    }

    const redirectToExternalTickets = () => {
        window.location.href = resources?.BuyerBookMoreTicketsUrl;
    }

    const mapTickets = () => {
        let ticketsSorted = [];

        if (tickets && tickets.length > 0) {
            ticketsSorted = [...tickets].sort((a, b) => a.id - b.id);
        }

        return (
            tickets &&
            tickets.length > 0 &&
            ticketsSorted.map((tt, index) => (
                <div key={tt.id} className="row align-items-center mb-2">
                    <div className="col-5 text-start">
                        <label className="ticket-name-label mb-0">
                            {tt.eventInfo?.name}
                        </label>
                    </div>
                    <div className="col-4 text-start">
                        <label className="ticket-name-label mb-0">
                            1x {tt.ticketTypeInfo?.name}
                        </label>
                    </div>
                    <div className="col-3 text-start d-flex align-items-center justify-content-end">
                        <label className="mb-0">
                            {tt.ticketType?.currency} {tt.ticketTypePrice.toFixed(2)}
                        </label>
                        <button
                            className="btn btn-link p-0 m-0 text-decoration-none ms-2"
                            onClick={() => removeTicketFromBasket(tt.id)}
                            aria-label="Remove ticket"
                            style={{ outline: 'none', boxShadow: 'none' }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="19"
                                height="19"
                                fill="none"
                                stroke="#4F5054"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                viewBox="0 0 24 24"
                                style={{ marginTop: '-2px' }}
                            >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>
            ))
        );
    };

    return (
        <>
            {loading ? (
                <>
                    <Loading
                        alignment="center"
                        color="#d3d3d3"
                        bgColor="transparent"
                        top="50%"
                        resources={resources}
                    />
                </>
            ) : (
                <>
                    <Header
                        language={language}
                        pagePath="address"
                        eventName={eventName}
                        eventDate={eventDate}
                        eventStartTime={eventStartTime}
                        eventCity={eventCity}
                        eventLocation={eventLocation}
                        isShoppingCartVisible={true}
                        numberOfTickets={order?.tickets?.length ?? 0}
                    />

                    <motion.div
                        className="page-container"
                        initial={{ opacity: 0, y: 30 }}   // start: invisible + lower
                        animate={{ opacity: 1, y: 0 }}    // end: visible + in place
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
                    >
                        <div className="container wrapper">

                            {/*<div className="row fw-bold mt-5">*/}
                            {/*    <div className="d-flex align-items-start">*/}
                            {/*        <img src={imgIconInfo} alt="Increase" width="18" height="18" className="me-2" />*/}
                            {/*        <span>{resources?.BuyerMandatoryFields}</span>*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                            <div className="row mt-5">
                                <div className="col-md-12 text-center">
                                    <span className="text-title">{resources?.OverviewTitle}</span>
                                </div>
                            </div>

                            <div className="tickets-box p-4 pt-3 mt-5">
                                <div className="row">
                                    <div className="col-md-12">
                                        {mapTickets()}
                                    </div>
                                </div>

                                <hr className="ticket-separator" />

                                <div className="row mt-2">
                                    <div className="col-6">
                                        <span className="text-subtitle">{resources?.OverviewTotal}</span>
                                    </div>
                                    <div className="col-6 text-end">
                                        <span className="text-subtitle">
                                            {order.currency} {order.total?.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-5">
                                <div className="col-md-12 text-center">
                                    <span className="text-title">{resources?.BuyerPleaseFill}</span>
                                </div>
                            </div>

                            <div className="tickets-box p-4 mt-5">
                                {/* Buyer */}
                                <div className="mb-4 row">
                                    <div className="col-md-12">
                                        <select className={`form-select form-select-custom mt-2`} value={buyerInfo.salutation} name="salutation" onChange={onInputChange}>
                                            <option value="">{resources?.BuyerSalutation}</option>
                                            <option value={resources?.BuyerSalutationMr}>{resources?.BuyerSalutationMr}</option>
                                            <option value={resources?.BuyerSalutationMs}>{resources?.BuyerSalutationMs}</option>
                                            {language === "en" && (
                                                <option value="Mrs.">Mrs.</option>
                                            )}
                                            <option value={resources?.BuyerSalutationDiverse}>{resources?.BuyerSalutationDiverse}</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="mb-4 row">
                                    <div className="col-md-12">
                                        <input
                                            type="text"
                                            name="firstName"
                                            className="form-control mt-2"
                                            placeholder={resources?.BuyerFirstname}
                                            value={buyerInfo.firstName}
                                            id="firstName"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="mb-4 row">
                                    <div className="col-md-12">
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-control mt-2"
                                            placeholder={resources?.BuyerLastname}
                                            value={buyerInfo.name}
                                            id="name"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="mb-4 row">
                                    <div className="col-md-12">
                                        <input
                                            type="text"
                                            name="street"
                                            className="form-control mt-2"
                                            placeholder={resources?.BuyerStreet}
                                            value={buyerInfo.street}
                                            id="street"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="mb-4 row">
                                    <div className="col-md-12">
                                        <input
                                            type="text"
                                            name="postCode"
                                            className="form-control mt-2"
                                            placeholder={resources?.BuyerPostcode}
                                            value={buyerInfo.postCode}
                                            id="postCode"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="mb-4 row">
                                    <div className="col-md-12">
                                        <input
                                            type="text"
                                            name="city"
                                            className="form-control mt-2"
                                            placeholder={resources?.BuyerCity}
                                            value={buyerInfo.city}
                                            id="city"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="mb-3 row">
                                    <div className="col-md-12">
                                        <div>
                                            <select
                                                className="form-select"
                                                aria-label=""
                                                id="countryId"
                                                name="countryId"
                                                value={buyerInfo.countryId}
                                                onChange={onInputChange}
                                            >
                                                {
                                                    countries.map((country, index) => (
                                                        <option value={country.id}>{country.name}</option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4 row">
                                    <div className="col-md-12">
                                        <input
                                            type="text"
                                            name="email"
                                            className="form-control mt-2"
                                            placeholder={resources?.BuyerEmail}
                                            value={buyerInfo.email}
                                            id="email"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="mb-4 row">
                                    <div className="col-md-12">
                                        <input
                                            type="text"
                                            name="confirmemail"
                                            className="form-control mt-2"
                                            placeholder={resources?.BuyerConfirmEmail}
                                            value={buyerInfo.confirmemail}
                                            id="confirmemail"
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>

                                {/*<div className="mb-4 row">*/}
                                {/*    <div className="col-md-12">*/}
                                {/*        <div className="form-check mt-2">*/}
                                {/*            <input*/}
                                {/*                className="form-check-input"*/}
                                {/*                type="checkbox"*/}
                                {/*                id="newsletter"*/}
                                {/*                name="newsletter"*/}
                                {/*                checked={buyerInfo.newsletter}*/}
                                {/*                onChange={e => setBuyerInfo({ ...buyerInfo, newsletter: e.target.checked })}*/}
                                {/*            />*/}
                                {/*            <label className="form-check-label" htmlFor="newsletter">*/}
                                {/*                {resources?.BuyerNewsletter}*/}
                                {/*            </label>*/}
                                {/*        </div>*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                {isError &&
                                    <div className="row text-start mt-2">
                                        <p className="text-error">{resources?.ErrorPleaseFillMandatoryFields}</p>
                                    </div>
                                }

                                {buyerInfo?.email !== buyerInfo?.confirmemail && buyerInfo?.confirmemail?.length > 0 &&
                                    <div className="row">
                                        <div className="col-12 text-end">
                                            <span className="error">{resources?.BuyerErrorPasswordMismatch}</span>
                                        </div>
                                    </div>
                                }

                                <div className="row mt-5">
                                    {/*<div className="col-6">*/}
                                    {/*    <button className="btn btn-custom" onClick={() => onBackClick()}>{resources?.Back}</button>*/}
                                    {/*</div>*/}
                                    <div className="col-12 text-end">
                                        <button className="btn btn-custom" onClick={() => onSubmit()}>{resources?.Next}</button>
                                    </div>
                                </div>
                            </div>

                            {/* Book More Tickets Section */}
                            {/*<div className="row mt-4 mb-4">*/}
                            {/*    <div className="col-12 text-center">*/}
                            {/*        <button*/}
                            {/*            className="btn btn-custom-secondary"*/}
                            {/*            onClick={onBookMoreTickets}*/}
                            {/*            style={{*/}
                            {/*                backgroundColor: "#293E6E",*/}
                            {/*                color: "white",*/}
                            {/*                padding: "10px 20px",*/}
                            {/*                borderRadius: "4px",*/}
                            {/*                border: "none",*/}
                            {/*                fontWeight: "600",*/}
                            {/*                textTransform: "uppercase"*/}
                            {/*            }}*/}
                            {/*        >*/}
                            {/*            {resources?.BuyerBookMoreTickets}*/}
                            {/*        </button>*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                            <div className="row mt-5">
                                <div className="col-md-12 text-center">
                                    <p className="mt-4">
                                        <small style={{ color: 'grey', fontSize: '10px' }}>powered by <a style={{ color: 'grey', textDecoration: 'none', fontSize: '13px' }} href="https://organizer.ticketino.com/de" target="_blank">TICKETINO</a></small>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </>
    )
}

export default Shipping